<script setup>
import { reactive, computed, onMounted, nextTick } from 'vue';
import { useTranslation } from 'i18next-vue';
import i18next from 'i18next';
import { Plus, Pencil, Trash2, ExternalLink } from 'lucide-vue-next';
import { EventSeries } from '@/common/model/event_series';
import { EventSeriesContent } from '@/common/model/event_series_content';
import { seriesPath } from '@/common/routing/public-paths';
import SeriesService from '@/client/service/series';
import SeriesEditor from './series-editor.vue';
import ConfirmDeleteDialog from '@/client/components/common/confirm-delete-dialog.vue';
import EmptyLayout from '@/client/components/common/empty_state.vue';
import LoadingMessage from '@/client/components/common/loading_message.vue';
import PillButton from '@/client/components/common/pill-button.vue';
import { useToast } from '@/client/composables/useToast';

const props = defineProps({
  calendarId: {
    type: String,
    required: true,
  },
  calendarUrlName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['seriesUpdated']);

const { t } = useTranslation('series', {
  keyPrefix: 'management',
});

const toast = useToast();
const seriesService = new SeriesService();
const currentLanguage = computed(() => i18next.language);

const state = reactive({
  series: [],
  isLoading: false,
  error: '',

  // Editor state
  showEditor: false,
  seriesToEdit: null,

  // Delete dialog state
  showDeleteDialog: false,
  seriesToDelete: null,
  isDeleting: '',

  // Focus management
  triggerElement: null,
});

/**
 * Load all series for the calendar
 */
async function loadSeries() {
  state.isLoading = true;
  state.error = '';

  try {
    state.series = await seriesService.loadSeries(props.calendarId);
  }
  catch (error) {
    console.error('Error loading series:', error);
    state.error = t('error_loading');
  }
  finally {
    state.isLoading = false;
  }
}

/**
 * Open the series editor for creating a new series
 */
function openCreateEditor() {
  const newSeries = new EventSeries(
    null, // ID will be generated by the server
    props.calendarId,
    '',
    null,
  );
  // Initialize with current language
  newSeries.addContent(EventSeriesContent.fromObject({
    language: currentLanguage.value,
    name: '',
    description: '',
  }));
  state.seriesToEdit = newSeries;
  state.showEditor = true;
}

/**
 * Open the series editor for editing an existing series
 */
function openEditEditor(series, event) {
  state.triggerElement = event?.currentTarget ?? null;
  // Create a copy of the series for editing
  const seriesToEdit = new EventSeries(
    series.id,
    series.calendarId,
    series.urlName,
    series.mediaId,
  );

  // Copy all content from the original series
  const languages = series.getLanguages();
  languages.forEach(lang => {
    const content = series.content(lang);
    if (content) {
      seriesToEdit.addContent(EventSeriesContent.fromObject({
        language: lang,
        name: content.name,
        description: content.description,
      }));
    }
  });

  state.seriesToEdit = seriesToEdit;
  state.showEditor = true;
}

/**
 * Close the series editor
 */
function closeEditor() {
  const trigger = state.triggerElement;
  state.showEditor = false;
  state.seriesToEdit = null;
  state.triggerElement = null;
  nextTick(() => trigger?.focus());
}

/**
 * Handle series saved from editor
 */
async function onSeriesSaved() {
  const wasCreate = !state.seriesToEdit?.id;
  await loadSeries();
  emit('seriesUpdated');
  if (wasCreate) {
    toast.success(t('success_created'));
  }
  else {
    toast.success(t('success_updated'));
  }
}

/**
 * Open delete confirmation dialog
 */
function confirmDelete(series, event) {
  state.triggerElement = event?.currentTarget ?? null;
  state.seriesToDelete = series;
  state.showDeleteDialog = true;
}

/**
 * Cancel delete dialog
 */
function cancelDelete() {
  const trigger = state.triggerElement;
  state.seriesToDelete = null;
  state.showDeleteDialog = false;
  state.isDeleting = '';
  state.triggerElement = null;
  nextTick(() => trigger?.focus());
}

/**
 * Delete the series
 */
async function deleteSeries() {
  if (!state.seriesToDelete) {
    return;
  }

  state.isDeleting = state.seriesToDelete.id;
  state.error = '';

  try {
    await seriesService.deleteSeries(state.seriesToDelete.id, props.calendarId);
    await loadSeries();
    cancelDelete();
    emit('seriesUpdated');
    toast.success(t('success_deleted'));
  }
  catch (error) {
    console.error('Error deleting series:', error);
    toast.error(t('error_delete_series'));
  }
  finally {
    state.isDeleting = '';
  }
}

// Load series when component mounts
onMounted(async () => {
  await loadSeries();
});
</script>

<template>
  <div class="vstack stack--lg" :aria-busy="state.isLoading ? 'true': 'false'">

    <!-- Error Display -->
    <div
      v-if="state.error"
      class="alert alert--error"
      role="alert"
      aria-live="polite"
    >
      {{ state.error }}
    </div>

    <LoadingMessage v-if="state.isLoading" :description="t('loading')" />

    <!-- Series List -->
    <div v-else-if="state.series.length > 0" class="series-content">
      <div class="tab-header">
        <h2 class="series-title">{{ t('title') }}</h2>
        <PillButton
          variant="primary"
          @click="openCreateEditor"
        >
          <Plus :size="20" :stroke-width="2" />
          {{ t('add_series_button') }}
        </PillButton>
      </div>

      <div class="series-list">
        <div
          v-for="series in state.series"
          :key="series.id"
          class="series-card"
        >
          <div class="series-info">
            <div class="series-info__name">
              {{ series.content(currentLanguage)?.name || series.urlName }}
            </div>
            <div class="series-info__meta">
              <span>{{ t('event_count', { count: series.eventCount || 0 }) }}</span>
            </div>
          </div>

          <div class="series-actions">
            <!--
              A series has no server-stamped public address the way a calendar
              does, so the shape comes from the shared builder. A plain href,
              not a RouterLink: the public series page is the site SPA's, and
              an in-SPA navigation would never reach the server that serves it.

              Unlike the calendar link elsewhere in this app, this is root-
              relative: the builder emits no origin, because that requires
              configuration only a server caller has, and the calendar link is
              absolute only because the server stamped it. So this link works
              in the browser but isn't copy-pasteable as-is. Neither link
              carries a locale prefix — Calendar.publicUrl is the origin plus
              calendarPath and nothing else — so both open the default-locale
              page. Locale is the site SPA's concern via localizedPath, which
              this app has no equivalent of.
            -->
            <a
              :href="seriesPath(props.calendarUrlName, series.urlName)"
              target="_blank"
              rel="noopener noreferrer"
              class="icon-button"
              :aria-label="t('view_public_page')"
            >
              <ExternalLink :size="20" :stroke-width="2" aria-hidden="true" />
            </a>
            <button
              type="button"
              class="icon-button"
              @click="openEditEditor(series, $event)"
              :disabled="state.isDeleting === series.id"
              :aria-label="t('edit_button')"
            >
              <Pencil :size="20" :stroke-width="2" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="icon-button icon-button--danger"
              @click="confirmDelete(series, $event)"
              :disabled="state.isDeleting === series.id"
              :aria-label="t('delete_button')"
            >
              <Trash2 :size="20" :stroke-width="2" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <EmptyLayout v-else
                 :title="t('no_series')"
                 :description="t('no_series_description')"
                 :guide="{ slug: 'guides/calendar-owners/series', key: 'series' }"
                 :guide-label="t('guide_link')">
      <button
        type="button"
        class="btn btn--cta btn--lg"
        @click="openCreateEditor"
        :disabled="state.isLoading"
      >
        <Plus :size="20" :stroke-width="2" />
        {{ t('add_series_button') }}
      </button>
    </EmptyLayout>

    <!-- Series Editor Modal -->
    <SeriesEditor
      v-if="state.showEditor && state.seriesToEdit"
      :series="state.seriesToEdit"
      @close="closeEditor"
      @saved="onSeriesSaved"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmDeleteDialog
      :visible="state.showDeleteDialog && !!state.seriesToDelete"
      :title="t('confirm_delete_title')"
      :message="t('confirm_delete_message')"
      :is-deleting="state.isDeleting === state.seriesToDelete?.id"
      :delete-label="t('delete_button')"
      :deleting-label="t('deleting')"
      :cancel-label="t('cancel_button')"
      modal-class="delete-series-modal"
      @confirm="deleteSeries"
      @close="cancelDelete"
    />
  </div>
</template>

<style scoped lang="scss">
@use '../../../assets/style/components/calendar-admin' as *;

.series-content {
  @include admin-section;
}

.series-header {
  @include admin-section-header;
}

.series-title {
  @include admin-section-title;
}

.series-list {
  @include admin-item-list;
}

.series-card {
  @include admin-card;

  &:hover {
    border-color: var(--pav-color-stone-300);

    @media (prefers-color-scheme: dark) {
      border-color: var(--pav-color-stone-600);
    }
  }
}

.series-info {
  @include admin-item-info;
}

.series-actions {
  @include admin-item-actions;
}

.icon-button {
  @include admin-icon-button;

  &--danger {
    @include admin-icon-button--danger;
  }
}

// Constrain modal width
:global(.delete-series-modal > div) {
  max-width: 500px !important;
}
</style>
